/**
 * SOS incident persistence on the LG Band device document (adafruit/{docId}.sos_incident).
 * Authenticated users may write adafruit/{id} (existing rules), and the backend import writes
 * with merge, so this field survives routine packets. Shared across operators; survives reloads.
 */
import { doc, setDoc, serverTimestamp, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase-config';

/**
 * The organisation's Atlas towers list (towers/{id}: name, location GeoPoint), for the SOS
 * "Nearest Tower" field. Loaded separately from the map's towerLocations, which is only fetched
 * when the role has viewTowers.
 */
export async function loadSosTowers(organizationId) {
  if (!organizationId) return [];
  try {
    const snap = await getDocs(query(collection(db, 'towers'), where('organizationId', '==', organizationId)));
    return snap.docs
      .map((d) => {
        const data = d.data() || {};
        const loc = data.location;
        const lat = loc?.latitude ?? data.latitude ?? data.coordinates?.lat;
        const lng = loc?.longitude ?? data.longitude ?? data.coordinates?.lng;
        return { id: d.id, name: data.name || d.id, coordinates: { lat: Number(lat), lng: Number(lng) } };
      })
      .filter((t) => Number.isFinite(t.coordinates.lat) && Number.isFinite(t.coordinates.lng));
  } catch (e) {
    console.warn('🆘 loadSosTowers failed:', e?.code || e);
    return [];
  }
}

export async function openSosIncident(firestoreDocId, openedAtIso, extra = {}) {
  if (!firestoreDocId) return;
  await setDoc(doc(db, 'adafruit', String(firestoreDocId)), {
    sos_incident: {
      status: 'open',
      opened_at: openedAtIso || new Date().toISOString(),
      opened_recorded_at: serverTimestamp(),
      closed_at: null,
      closed_by: null,
      ...extra,
    },
  }, { merge: true });
}

/**
 * Persist the bell entry for an SOS in `notifications`, the same org-scoped feed the geofence
 * analyzer writes entry/exit to, so every operator's bell shows it and it survives reloads.
 * Deterministic id => the first client to see the SOS creates it; later clients' creates are
 * rejected by the rules (no update allowed) and ignored here.
 */
export async function createSosNotification(notificationId, organizationId, fields = {}) {
  if (!notificationId || !organizationId) return false;
  try {
    await setDoc(doc(db, 'notifications', String(notificationId)), {
      eventType: 'sos',
      category: 'lg_band_sos',
      organizationId,
      timestamp: serverTimestamp(),
      createdAt: serverTimestamp(),
      ...fields,
    });
    return true;
  } catch (e) {
    if (e?.code !== 'permission-denied') console.warn('🆘 createSosNotification failed:', e?.code || e);
    return false;
  }
}

export async function closeSosIncident(firestoreDocId, closedBy) {
  if (!firestoreDocId) return;
  await setDoc(doc(db, 'adafruit', String(firestoreDocId)), {
    sos_incident: {
      status: 'closed',
      closed_at: new Date().toISOString(),
      closed_recorded_at: serverTimestamp(),
      closed_by: closedBy || null,
    },
  }, { merge: true });
}
